// src/utils/services/locationService.ts
import { z } from 'zod';

export const InitialLocationSource = z.enum(['no', 'browser', 'api']);
export type InitialLocationSource = z.infer<typeof InitialLocationSource>;

class LocationService {
  private static instance: LocationService;

  private constructor() { }

  public static getInstance(): LocationService {
    if (!this.instance) {
      this.instance = new LocationService();
    }
    return this.instance;
  }

  /**
   * Attempts to get the user's location based on the specified source.
   * This method is asynchronous and returns a Promise.
   * @param from The source to get the location from ('no', 'browser', 'api').
   * @returns A Promise that resolves with { lat, lng } or null if location cannot be obtained.
   */
  public async getLocation(from: InitialLocationSource): Promise<{ lat: number, lng: number } | null> {
    switch (from) {
      case 'no':
        return null;

      case 'browser':
        if ("geolocation" in navigator) {
          return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              position => {
                resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
              },
              error => {
                console.warn(`[LocationService] Unable to retrieve location from browser: ${error.message}`);
                resolve(null);
              },
              {
                enableHighAccuracy: true,
                maximumAge: 0
              }
            );
          });
        }
        console.warn('[LocationService] Geolocation is not supported by this browser.');
        return null;

      case 'api':
        console.warn('[LocationService] API-based location retrieval is selected but not yet implemented.');
        return null;

      default:
        console.warn('[LocationService] Unknown location source specified:', from);
        return null;
    }
  }
}

export const locationService = LocationService.getInstance();
